import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import Link2Icon from '@mui/icons-material/Link';
import { TrackerDataType } from '../../shared/constants/constants';
import * as queryAdderPopupStyles from '../QueryAdder/queryAdderPopupStyles';

const {
  adderHeaderTitleSx,
  queryAdderPopupFieldLabelSx,
  queryAdderPopupFormSpacing,
  queryAdderPopupInputSx,
  queryAdderPopupActionButtonSx,
  utilityCancelButtonSx,
  utilityModalContentSx,
  utilityModalDialogSx,
  utilityModalFooterSx,
  utilityModalHeaderSx,
  utilityModalIconTileSx,
  utilityModalPaperSx,
  utilityPrimaryActionButtonSx,
} = queryAdderPopupStyles;

export default function EditCommsMaterialDialog({ open, onClose, fileData, onSaveItem, onDeleteItem, type }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [path, setPath] = useState('');

  useEffect(() => {
    if (fileData) {
      setName(fileData.name || '');
      setDescription(fileData.description || '');
      setPath(fileData.path || '');
    } else {
      setName('');
      setDescription('');
      setPath('');
    }
  }, [fileData]);

  const handleSave = () => {
    if (!name.trim()) {
      alert('File Name is required.');
      return;
    }
    if (!path.trim() && fileData?.type === 'url') {
      alert('Link to File is required for URL types.');
      return;
    }
    if (!path.trim() && fileData?.type !== 'url' && !fileData?._id) {
      alert('File Path is required for new file uploads.');
      return;
    }

    onSaveItem({
      ...fileData,
      name,
      description,
      path,
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${fileData?.name}"?`)) {
      onDeleteItem(fileData);
    }
  };

  const isUrlType = fileData?.type === 'url';
  const isStoryType = type === TrackerDataType.MEDIA_INTERACTION;
  const nonStoryModalTitle = isUrlType ? 'Edit Link to File' : 'Edit File';
  const modalTitle = isStoryType ? 'Edit Story' : nonStoryModalTitle;
  const nameFieldLabel = isStoryType ? 'Title' : 'File Name';
  const pathFieldLabel = isStoryType ? 'URL' : 'Link to File';
  const isPathRequired = isUrlType || (!fileData?._id && !isUrlType);
  const modalFieldLabelSx = {
    ...queryAdderPopupFieldLabelSx,
    fontWeight: 500,
  };
  const renderRequiredLabel = (label, required = false) => (
    <Typography sx={modalFieldLabelSx}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'primary.main' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={utilityModalDialogSx}
      PaperProps={{ sx: utilityModalPaperSx }}
    >
      <DialogTitle sx={utilityModalHeaderSx}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={utilityModalIconTileSx}>
            <Link2Icon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={adderHeaderTitleSx}>{modalTitle}</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ ...utilityModalContentSx, pt: isUrlType ? '24px !important' : 4.5 }}>
        <Stack spacing={queryAdderPopupFormSpacing}>
          <Box>
            {renderRequiredLabel(nameFieldLabel, true)}
            <TextField
              autoFocus
              id="name"
              type="text"
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={queryAdderPopupInputSx}
            />
          </Box>
          <Box>
            <Typography sx={modalFieldLabelSx}>Description</Typography>
            <TextField
              id="description"
              type="text"
              fullWidth
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
              sx={queryAdderPopupInputSx}
            />
          </Box>
          <Box>
            {renderRequiredLabel(pathFieldLabel, isPathRequired)}
            <TextField
              id="path"
              type="text"
              fullWidth
              variant="outlined"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              required={isPathRequired}
              disabled={!isUrlType && fileData?._id}
              sx={queryAdderPopupInputSx}
            />
            {!isUrlType && fileData?._id && (
              <Typography sx={{ mt: 0.5, fontSize: '13px', lineHeight: 1.5, color: '#6C737F' }}>
                Uploaded file paths cannot be changed here. Delete and re-upload to replace the file.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ ...utilityModalFooterSx, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} variant="outlined" sx={utilityCancelButtonSx}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          disabled={!fileData?._id}
          sx={{
            ...queryAdderPopupActionButtonSx,
            color: '#DC2626',
            borderColor: '#F3B4B4',
            '&:hover': {
              borderColor: '#E57373',
              backgroundColor: '#FFF5F5',
            },
          }}
        >
          Delete
        </Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} sx={utilityPrimaryActionButtonSx}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditCommsMaterialDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fileData: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    path: PropTypes.string,
    type: PropTypes.string,
  }),
  onSaveItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  type: PropTypes.oneOf([
    TrackerDataType.MEDIA_INTERACTION,
    TrackerDataType.SERVICE,
    TrackerDataType.ENGAGEMENT,
    TrackerDataType.CONTENT_LOG,
  ]),
};
